import { useState, useRef, useEffect } from "react";
import FormLoading from "../../components/loadding/FormLoading";
import { useNavigate, Link } from "react-router-dom";
import bgLogin from "../../images/bg-login.png";
import Swal from "sweetalert2";
import { Ward, District, Province } from '../../lib/Types';

const Register = () => {
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(1);
  const [customerId, setCustomerId] = useState("");
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [provinceId, setProvinceId] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [wardId, setWardId] = useState("");
  const selectedProvince = provinces.find(p => p.Id === provinceId);
  const districts = selectedProvince?.Districts || [];
  const selectedDistrict = districts.find(d => d.Id === districtId);
  const wards = selectedDistrict?.Wards || [];

  const [form, setForm] = useState({
    last_name: "",
    first_name: "",
    email: "",
    tel: "",
    password: "",
    verify_code: "",
    province: "",
    district: "",
    ward: "",
    note_address: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.errors) {
          setErrors(data.errors);
        } else if (data.message) {
          setErrors({
            email: [data.message],
          });
        }
        return;
      }

      if (data.next_step === "verify") {
        setActiveTab(2);
        setErrors({});
      }

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (loading) return;

    const verifyCode = otp.join("");

    if (verifyCode.length !== 6) {
      setErrors({
        code: ["Vui lòng nhập đủ 6 chữ số"],
      });
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/verify-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email: form.email,
            code: verifyCode,
          }),
        }
      );

      const data = await res.json();
      // console.log(data);

      if (!res.ok) {
        if (data.errors) {
          setErrors(data.errors);
        } else if (data.message) {
          setErrors({
            code: [data.message],
          });
        }
        return;
      }

      setCustomerId(data.customer_id);
      setErrors({});
      setActiveTab(3);
    } catch (error) {
      console.error(error);
      setErrors({
        code: ["Có lỗi xảy ra, vui lòng thử lại"],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setErrors({});
    setLoading(true);

    const provinceName =
      provinces.find(p => String(p.Id) === String(provinceId))?.Name || "";

    const districtName =
      districts.find(d => String(d.Id) === String(districtId))?.Name || "";

    const wardName =
      wards.find(w => String(w.Id) === String(wardId))?.Name || "";

    const payload = {
      ...form,
      province: provinceName,
      district: districtName,
      ward: wardName,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/customer-update/${customerId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (data.errors) {
          setErrors(data.errors);
        }
        return;
      }

      setLoading(false);

      await Swal.fire({
        icon: "success",
        title: "Thành công",
        text: "Cập nhật thông tin thành công!",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/login");
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }

    // đủ 6 số → gán vào form.verify_code
    if (newOtp.every((d) => d !== "")) {
      setForm((prev) => ({
        ...prev,
        verify_code: newOtp.join(""),
      }));
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasteData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (!pasteData) return;

    const newOtp = [...otp];

    for (let i = 0; i < pasteData.length; i++) {
      newOtp[i] = pasteData[i];
    }

    setOtp(newOtp);

    // focus ô cuối cùng đã paste
    const lastIndex = pasteData.length - 1;
    inputsRef.current[lastIndex]?.focus();
  };

  const resetOtp = () => {
    setOtp(Array(6).fill(""));
    inputsRef.current[0]?.focus();
  };

  useEffect(() => {
    fetch("/data/vietnam-address.json")
      .then(res => res.json())
      .then(data => setProvinces(data));
  }, []);

  return (
    <section className="sec-login !h-screen" style={{ backgroundImage: `url(${bgLogin})` }}>
      <div className={`container ${activeTab === 3 ? '!max-w-[75rem]' : ''}`}>
        <div className="content rounded-3xl">
          <h2 className="text-center text-[2.8rem] text-[#046DB3] font-bold sm:text-[4rem] -mb-4">Đăng ký thành viên</h2>
          {activeTab === 1 && (
            <form onSubmit={handleRegister} className="max-w-[50rem] mt-[6rem] mx-auto flex flex-col gap-8 px-8 sm:px-0">
              <div className="flex flex-col gap-2">
                <label className="text-[1.4rem]">Địa chỉ email<span className="text-red-500 ml-1">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={`border rounded-xl px-6 py-4 text-2xl ${errors.email ? "border border-[#F32637]" : ""}`}
                />
                {errors.email && (
                  <p className="text-[#F32637] text-base">
                    {errors.email[0]}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[1.4rem]">Mật khẩu<span className="text-red-500 ml-1">*</span></label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  className={`border rounded-xl px-6 py-4 text-2xl ${errors.password ? "border border-[#F32637]" : ""}`}
                />
                {errors.password && (
                  <p className="text-[#F32637] text-base">
                    {errors.password[0]}
                  </p>
                )}
              </div>

              <button type="submit" className="btn-dark font-semibold">
                Đăng ký
              </button>
            </form>
          )}

          {activeTab === 2 && (
            <form onSubmit={handleVerifyCode} className="max-w-[50rem] mt-[6rem] mx-auto flex flex-col gap-8 px-8 sm:px-0">
              <div className="flex justify-center gap-4">
                {otp.map((value, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputsRef.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={value}
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    onPaste={handleOtpPaste}
                    className={`w-20 h-20 text-4xl text-center rounded-xl border
                                ${errors.verify_code ? "border-[#F32637]" : "border-gray-300"}
                                focus:outline-none focus:ring-2 focus:ring-[#046DB3]
                              `}
                  />
                ))}
              </div>
              {errors.code && (
                <p className="text-red-500 text-xl text-center">
                  {errors.code[0]}
                </p>
              )}
              <div className="flex gap-6 mt-6">
                <button
                  onClick={() => {
                    resetOtp();
                    setErrors({});
                    setActiveTab(1);
                  }}
                  type="button" className="btn-dark font-semibold !bg-black">
                  Quay lại
                </button>
                <button type="submit" className="btn-dark font-semibold">
                  Xác nhận
                </button>
              </div>
            </form>
          )}

          {activeTab === 3 && (
            <form onSubmit={handleUpdate} className="max-w-[65rem] mt-[6rem] mx-auto flex flex-col gap-8 px-8 sm:px-0">
              <div className="flex gap-8">
                <div className="flex flex-col gap-2 w-full">
                  <label className="text-[1.4rem]">Họ<span className="text-red-500 ml-1">*</span></label>
                  <input
                    name="first_name"
                    value={form.first_name}
                    onChange={handleChange}
                    className={`border rounded-xl px-6 py-4 text-2xl ${errors.first_name ? "border border-[#F32637]" : ""}`}
                    placeholder="Nguyễn Văn"
                  />
                  {errors.first_name && (
                    <p className="text-[#F32637] text-base">
                      {errors.first_name[0]}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <label className="text-[1.4rem]">Tên<span className="text-red-500 ml-1">*</span></label>
                  <input
                    name="last_name"
                    value={form.last_name}
                    onChange={handleChange}
                    className={`border rounded-xl px-6 py-4 text-2xl ${errors.last_name ? "border border-[#F32637]" : ""}`}
                    placeholder="A"
                  />
                  {errors.last_name && (
                    <p className="text-[#F32637] text-base">
                      {errors.last_name[0]}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[1.4rem]">Số điện thoại<span className="text-red-500 ml-1">*</span></label>
                <input
                  type="number"
                  name="tel"
                  value={form.tel}
                  onChange={handleChange}
                  className={`border rounded-xl px-6 py-4 text-2xl ${errors.tel ? "border border-[#F32637]" : ""}`}
                />
                {errors.tel && (
                  <p className="text-[#F32637] text-base">
                    {errors.tel[0]}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Province */}
                <div className="flex flex-col gap-2">
                  <label className="text-[1.4rem]">
                    Tỉnh / Thành phố<span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    name="province_id"
                    value={provinceId}
                    onChange={e => {
                      setProvinceId(e.target.value);
                      setDistrictId("");
                      setWardId("");
                    }}
                    className={`border rounded-xl px-4 py-4 text-2xl appearance-auto ${errors.province ? "border border-[#F32637]" : ""}`}
                  >
                    <option value="">-- Chọn tỉnh / thành --</option>
                    {provinces.map(p => (
                      <option key={p.Id} value={p.Id}>
                        {p.Name}
                      </option>
                    ))}
                  </select>
                  {errors.province && (
                    <p className="text-[#F32637] text-base">
                      {errors.province[0]}
                    </p>
                  )}
                </div>

                {/* District */}
                <div className="flex flex-col gap-2">
                  <label className="text-[1.4rem]">
                    Quận / Huyện<span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    name="district_id"
                    value={districtId}
                    onChange={e => {
                      setDistrictId(e.target.value);
                      setWardId("");
                    }}
                    disabled={!provinceId}
                    className={`border rounded-xl px-4 py-4 text-2xl appearance-auto disabled:bg-gray-100 ${errors.district ? "border border-[#F32637]" : ""}`}
                  >
                    <option value="">-- Chọn quận / huyện --</option>
                    {districts.map(d => (
                      <option key={d.Id} value={d.Id}>
                        {d.Name}
                      </option>
                    ))}
                  </select>
                  {errors.district && (
                    <p className="text-[#F32637] text-base">
                      {errors.district[0]}
                    </p>
                  )}
                </div>

                {/* Ward */}
                <div className="flex flex-col gap-2">
                  <label className="text-[1.4rem]">
                    Phường / Xã<span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    name="ward_id"
                    value={wardId}
                    onChange={e => setWardId(e.target.value)}
                    disabled={!districtId}
                    className={`border rounded-xl px-4 py-4 text-2xl appearance-auto disabled:bg-gray-100 ${errors.ward ? "border border-[#F32637]" : ""}`}
                  >
                    <option value="">-- Chọn phường / xã --</option>
                    {wards.map(w => (
                      <option key={w.Id} value={w.Id}>
                        {w.Name}
                      </option>
                    ))}
                  </select>
                  {errors.ward && (
                    <p className="text-[#F32637] text-base">
                      {errors.ward[0]}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[1.4rem]">Ghi chú</label>
                <input
                  name="note_address"
                  value={form.note_address}
                  onChange={handleChange}
                  placeholder="Chi tiết địa chỉ: hẻm, ngách, khu, số nhà, ...."
                  className={`border rounded-xl px-6 py-4 text-2xl ${errors.note_address ? "border border-[#F32637]" : ""}`}
                />
                {errors.note_address && (
                  <p className="text-[#F32637] text-base">
                    {errors.note_address[0]}
                  </p>
                )}
              </div>
              <button type="submit" className="btn-dark font-semibold">
                Hoàn tất
              </button>
            </form>
          )}

          <div className="mt-[2.5rem] text-center text-2xl flex flex-col items-center">
            <Link to="/login" className="hover:underline duration-300">Đã có tài khoản</Link>
            {/* <a href="" className="repassword">パスワードを忘れた方はこちら</a> */}
          </div>
        </div>

        {/* <div className="content-ft">
            <p>powered by </p>
          </div> */}
      </div>

      {loading && (
        <FormLoading loading={loading} />
      )}

    </section>
  );
};

export default Register;
